import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Sparkles, BookOpen, ListChecks, Layers, GraduationCap, Image as ImageIcon,
  Upload, FileText, Loader2, Download, Trash2, ExternalLink, AlertTriangle,
  Wand2, RefreshCw, FileUp, X, Share2, Edit3, MessageSquare, CheckCircle2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOrgId } from '../../hooks/useOrgId';
import { useAuth } from '../../context/AuthContext';
import { oustazApi, fileUrl } from '../../api/oustaz';
import OustazChat from '../../components/school/OustazChat';
import OustazBooks from '../../components/school/OustazBooks';
import { PageHeader, EmptyState, InlineSpinner } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const Panel = ({ className = '', children, ...rest }) => (
  <div className={`bg-card border border-border rounded-md shadow-sm ${className}`} {...rest}>{children}</div>
);

const TYPE_META = {
  interactive_lesson: { icon: BookOpen, label: 'Interactive Lesson', desc: 'Interactive HTML lesson with exercises, feedback, scoring, inline SVG diagrams & AI images where useful', color: '#3B6FB6' },
  quiz: { icon: ListChecks, label: 'Quiz', desc: 'Mixed questions with a complete answer key', color: '#E0A53B' },
  flash_cards: { icon: Layers, label: 'Flash Cards', desc: 'Front/back study cards for key concepts', color: '#3E9C7B' },
  explanation: { icon: GraduationCap, label: 'Explanation + Exercise', desc: 'Concept explanation with graded practice', color: '#8A63B8' },
  infographic: { icon: ImageIcon, label: 'Infographic', desc: 'Visual one-page educational summary', color: '#D9482B' },
};

const DEFAULT_PROVIDER = {
  interactive_lesson: 'claude', quiz: 'claude', flash_cards: 'claude', explanation: 'claude', infographic: 'openai',
};

const STEP_LABELS = {
  queued: 'Queued…', reading_pdf: 'Reading document…', preparing_prompt: 'Preparing prompt…',
  loading_reference: 'Loading reference…', uploading_pdf: 'Attaching PDF…', extracting_text: 'Extracting text…',
  generating: 'Generating content with AI…', generating_images: 'Generating images…',
  processing_output: 'Processing output…', saving_html: 'Saving…', completed: 'Completed', failed: 'Failed',
};

export default function OustazPage() {
  const orgId = useOrgId();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('create');
  const [config, setConfig] = useState(null);

  // create state
  const [selectedType, setSelectedType] = useState('interactive_lesson');
  const [inputMode, setInputMode] = useState('pdf');
  const [file, setFile] = useState(null);
  const [pastedText, setPastedText] = useState('');
  const [jsonFile, setJsonFile] = useState(null);
  const [jsonInfo, setJsonInfo] = useState(null);      // { json_url, book_title, chapters:[...] }
  const [jsonUploading, setJsonUploading] = useState(false);
  const [chunkIndex, setChunkIndex] = useState('');    // '' = whole book / use query
  const [chunkQuery, setChunkQuery] = useState('');
  const [aiProvider, setAiProvider] = useState('claude');
  const [classes, setClasses] = useState([]);
  const [targetClassId, setTargetClassId] = useState('');
  const [allSections, setAllSections] = useState(true);
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const [visibleToStudents, setVisibleToStudents] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // library state
  const [generations, setGenerations] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // share dialog state
  const [shareTarget, setShareTarget] = useState(null);
  const [shareClassId, setShareClassId] = useState('');
  const [shareAllSections, setShareAllSections] = useState(true);
  const [shareSectionIds, setShareSectionIds] = useState([]);
  const [shareVisible, setShareVisible] = useState(true);
  const [shareSaving, setShareSaving] = useState(false);

  // rename dialog state
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [autoNaming, setAutoNaming] = useState(false);

  const loadConfig = useCallback(async () => {
    try { setConfig(await oustazApi.config(orgId)); } catch { /* ignore */ }
  }, [orgId]);

  const loadGenerations = useCallback(async () => {
    setLibLoading(true);
    try { setGenerations(await oustazApi.listGenerations(orgId)); }
    catch { toast.error('Failed to load your lessons'); }
    finally { setLibLoading(false); }
  }, [orgId]);

  useEffect(() => { if (orgId) loadConfig(); }, [orgId, loadConfig]);
  useEffect(() => { if (tab === 'library' && orgId) loadGenerations(); }, [tab, orgId, loadGenerations]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  useEffect(() => { setAiProvider(DEFAULT_PROVIDER[selectedType] || 'claude'); }, [selectedType]);
  useEffect(() => { setSelectedSectionIds([]); setAllSections(true); }, [targetClassId]);
  useEffect(() => {
    if (!orgId) return;
    oustazApi.classes(orgId).then(setClasses).catch(() => setClasses([]));
  }, [orgId]);

  const targetClass = classes.find((c) => c.id === targetClassId);
  const shareClass = classes.find((c) => c.id === shareClassId);

  const usesClaude = aiProvider === 'claude';
  const blockedByKeys = config && ((usesClaude && !config.has_claude_key) || (aiProvider === 'openai' && !config.has_openai_key));

  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    // Generation can take minutes; individual polls may hit a transient
    // gateway timeout (524) while the job keeps running server-side. Tolerate
    // a handful of consecutive errors before giving up so the UI recovers.
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;
    pollRef.current = setInterval(async () => {
      try {
        const j = await oustazApi.getJob(jobId);
        consecutiveErrors = 0;
        setJob(j);
        if (j.status === 'completed') {
          clearInterval(pollRef.current);
          setGenerating(false);
          setResult(j.result);
          toast.success('Content generated!');
          loadGenerations();
        } else if (j.status === 'failed') {
          clearInterval(pollRef.current);
          setGenerating(false);
          toast.error(j.error || 'Generation failed');
        }
      } catch {
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          clearInterval(pollRef.current);
          setGenerating(false);
          toast.error('Lost connection to job');
        }
        // otherwise keep polling — the job is likely still running
      }
    }, 3000);
  };

  const handleGenerate = async () => {
    if (blockedByKeys) { toast.error('Configure the required API key in Settings first'); return; }
    if (inputMode === 'pdf' && !file) { toast.error('Please choose a PDF'); return; }
    if (inputMode === 'text' && pastedText.trim().length < 30) { toast.error('Please paste at least a few sentences'); return; }
    if (inputMode === 'json' && !jsonInfo) { toast.error('Please upload a JSON chunk book'); return; }
    if (inputMode === 'json' && chunkIndex === '' && !chunkQuery.trim()) { toast.error('Pick a chapter or describe what to use'); return; }
    setGenerating(true);
    setResult(null);
    setJob({ step: 'queued', progress: 5, status: 'processing' });
    try {
      let pdf_url = null, pdf_filename = null, chapter_text = '';
      let json_url = null, chunk_index = null, chunk_query = null;
      if (inputMode === 'pdf') {
        const fd = new FormData();
        fd.append('file', file);
        const up = await oustazApi.uploadPdf(orgId, fd);
        pdf_url = up.pdf_url; pdf_filename = up.pdf_filename;
      } else if (inputMode === 'json') {
        json_url = jsonInfo.json_url;
        chunk_index = chunkIndex === '' ? null : Number(chunkIndex);
        chunk_query = chunkQuery.trim() || null;
      } else {
        chapter_text = pastedText.trim();
      }
      const { job_id } = await oustazApi.generate({
        content_type: selectedType, organization_id: orgId, pdf_url, pdf_filename, chapter_text,
        json_url, chunk_index, chunk_query,
        ai_provider: aiProvider,
        class_id: targetClassId || null,
        section_ids: targetClassId && !allSections ? selectedSectionIds : [],
        visible_to_students: visibleToStudents,
      });
      pollJob(job_id);
    } catch (e) {
      setGenerating(false);
      toast.error(e?.response?.data?.detail || 'Failed to start generation');
    }
  };

  const openHtml = (url) => { if (url) window.open(fileUrl(url), '_blank', 'noopener'); };

  const [viewerGen, setViewerGen] = useState(null);
  const openViewer = (g) => { if (g?.html_url) setViewerGen(g); };

  const [publishingId, setPublishingId] = useState(null);
  const handlePublish = async (genId) => {
    setPublishingId(genId);
    try {
      await oustazApi.publishGeneration(genId);
      toast.success('Published — draft flag removed');
      await loadGenerations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to publish');
    } finally { setPublishingId(null); }
  };

  const downloadZip = async (genId, title) => {
    try {
      const res = await axios.get(oustazApi.downloadUrl(genId), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${(title || 'lesson').replace(/\s+/g, '_')}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await oustazApi.deleteGeneration(deleteTarget.gen_id);
      toast.success('Deleted');
      setDeleteTarget(null);
      loadGenerations();
    } catch { toast.error('Delete failed'); }
  };

  const openShareDialog = (g) => {
    setShareTarget(g);
    setShareClassId(g.class_id || '');
    const hasSpecificSections = (g.section_ids || []).length > 0;
    setShareAllSections(!hasSpecificSections);
    setShareSectionIds(g.section_ids || []);
    setShareVisible(g.class_id ? (g.visible_to_students !== false) : true);
  };

  const saveSharing = async () => {
    if (!shareTarget) return;
    setShareSaving(true);
    try {
      const updated = await oustazApi.updateSharing(shareTarget.gen_id, {
        class_id: shareClassId || null,
        section_ids: shareClassId && !shareAllSections ? shareSectionIds : [],
        visible_to_students: shareVisible,
      });
      setGenerations((prev) => prev.map((x) => (x.gen_id === updated.gen_id ? { ...x, ...updated } : x)));
      toast.success(shareClassId ? 'Sharing updated' : 'Sharing removed');
      setShareTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update sharing');
    } finally { setShareSaving(false); }
  };

  const removeSharing = () => { setShareClassId(''); setShareAllSections(true); setShareSectionIds([]); };

  const openRename = (g) => { setRenameTarget(g); setRenameValue(g.title || ''); };

  const saveRename = async () => {
    if (!renameTarget) return;
    const title = renameValue.trim();
    if (!title) { toast.error('Title cannot be empty'); return; }
    setRenameSaving(true);
    try {
      const updated = await oustazApi.renameGeneration(renameTarget.gen_id, title);
      setGenerations((prev) => prev.map((x) => (x.gen_id === updated.gen_id ? { ...x, ...updated } : x)));
      toast.success('Lesson renamed');
      setRenameTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to rename');
    } finally { setRenameSaving(false); }
  };

  const runAutoName = async () => {
    setAutoNaming(true);
    try {
      const res = await oustazApi.autoNameGenerations(orgId);
      const n = res?.updated || 0;
      toast.success(n > 0 ? `Renamed ${n} lesson${n > 1 ? 's' : ''} from content` : 'All lessons already have custom names');
      if (n > 0) loadGenerations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-name failed');
    } finally { setAutoNaming(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f && f.type === 'application/pdf') setFile(f);
    else toast.error('Please drop a PDF file');
  };

  return (
    <div className="space-y-6" data-testid="oustaz-page">
      <PageHeader
        icon={Sparkles}
        title="OUSTAZ.AI"
        subtitle="Generate ready-to-use educational content from any chapter PDF or text"
      />

      {config && (!config.has_claude_key || !config.has_openai_key) && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="oustaz-keys-banner">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">
            {!config.has_claude_key && 'Anthropic (Claude) key not set. '}
            {!config.has_openai_key && 'OpenAI key not set (needed for images & infographics). '}
            Configure them in Settings → Integrations.
          </span>
          {config.is_admin && (
            <Button size="sm" variant="outline" onClick={() => navigate('/settings')} data-testid="oustaz-go-settings">Open Settings</Button>
          )}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="oustaz-tabs">
          <TabsTrigger value="create" data-testid="oustaz-tab-create"><Wand2 className="w-4 h-4 mr-1.5" />Create</TabsTrigger>
          <TabsTrigger value="books" data-testid="oustaz-tab-books"><BookOpen className="w-4 h-4 mr-1.5" />Books</TabsTrigger>
          <TabsTrigger value="chat" data-testid="oustaz-tab-chat"><MessageSquare className="w-4 h-4 mr-1.5" />AI Chat</TabsTrigger>
          <TabsTrigger value="library" data-testid="oustaz-tab-library"><Layers className="w-4 h-4 mr-1.5" />My Lessons</TabsTrigger>
          {config?.is_admin && <TabsTrigger value="prompts" data-testid="oustaz-tab-prompts"><FileText className="w-4 h-4 mr-1.5" />Prompts</TabsTrigger>}
        </TabsList>

        {/* CREATE */}
        <TabsContent value="create" className="space-y-5 mt-4">
          {/* Content type picker */}
          <div>
            <h3 className="text-sm font-semibold mb-3">1. Choose what to create</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(TYPE_META).map(([key, meta]) => {
                const Icon = meta.icon;
                const active = selectedType === key;
                return (
                  <button
                    key={key} type="button" onClick={() => setSelectedType(key)} data-testid={`oustaz-type-${key}`}
                    className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${active ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'border-border hover:border-primary/40'}`}
                  >
                    <span className="rounded-md p-2 text-white flex-shrink-0" style={{ backgroundColor: meta.color }}><Icon className="w-5 h-5" /></span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">{meta.label}</span>
                      <span className="block text-xs text-muted-foreground leading-snug">{meta.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Source input */}
          <Panel className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">2. Provide the source material</h3>
              <div className="flex gap-1 rounded-md border border-border p-0.5">
                <button type="button" onClick={() => setInputMode('pdf')} className={`px-3 py-1 text-xs rounded ${inputMode === 'pdf' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`} data-testid="oustaz-mode-pdf">PDF</button>
                <button type="button" onClick={() => setInputMode('text')} className={`px-3 py-1 text-xs rounded ${inputMode === 'text' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`} data-testid="oustaz-mode-text">Paste text</button>
                <button type="button" onClick={() => setInputMode('json')} className={`px-3 py-1 text-xs rounded ${inputMode === 'json' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`} data-testid="oustaz-mode-json">JSON (chunk book)</button>
              </div>
            </div>

            {inputMode === 'json' ? (
              <div className="space-y-3" data-testid="oustaz-json-panel">
                <input
                  type="file" accept=".json,application/json"
                  data-testid="oustaz-json-input"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    setJsonFile(f); setJsonInfo(null); setChunkIndex(''); setChunkQuery('');
                    setJsonUploading(true);
                    try {
                      const fd = new FormData(); fd.append('file', f);
                      const info = await oustazApi.uploadJson(orgId, fd);
                      setJsonInfo(info);
                      toast.success(`Loaded ${info.chapter_count} chapters from ${info.book_title || f.name}`);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Failed to read JSON');
                    } finally { setJsonUploading(false); }
                  }}
                  className="block w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:text-xs file:font-medium hover:file:bg-primary/90 cursor-pointer"
                />
                {jsonUploading && <p className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Reading chunk book…</p>}
                {jsonInfo && (
                  <div className="rounded-lg border border-border p-3 space-y-3" data-testid="oustaz-json-info">
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{jsonInfo.book_title}</span>
                      {jsonInfo.subject ? ` · ${jsonInfo.subject}` : ''}{jsonInfo.grade ? ` · ${jsonInfo.grade}` : ''}
                      {jsonInfo.language ? ` · ${String(jsonInfo.language).toUpperCase()}` : ''} · {jsonInfo.chapter_count} chapters
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Choose a chapter</label>
                      <select
                        value={chunkIndex}
                        onChange={(e) => setChunkIndex(e.target.value)}
                        data-testid="oustaz-json-chapter"
                        className="w-full text-sm rounded-md border border-border bg-background px-3 py-2"
                      >
                        <option value="">— Whole book (or use the instruction below) —</option>
                        {jsonInfo.chapters.map((c) => (
                          <option key={c.index} value={c.index}>
                            {`${c.index + 1}. ${c.title}${c.first_page ? ` (p.${c.first_page})` : ''} · ${c.chars} chars`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Or describe what to use / do</label>
                      <input
                        type="text" value={chunkQuery}
                        onChange={(e) => setChunkQuery(e.target.value)}
                        placeholder="e.g. 'Chapter 6', 'the continuity section', 'make a quiz on limits'"
                        data-testid="oustaz-json-query"
                        className="w-full text-sm rounded-md border border-border bg-background px-3 py-2"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : inputMode === 'pdf' ? (
              <div
                onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border py-8 cursor-pointer hover:border-primary/50 transition-colors"
                data-testid="oustaz-dropzone"
              >
                <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="oustaz-file-input" />
                {file ? (
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-5 h-5 text-primary" />
                    <span className="font-medium">{file.name}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <>
                    <FileUp className="w-8 h-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Drag & drop a chapter PDF, or click to browse</p>
                    <p className="text-xs text-muted-foreground">Max 15MB · scanned PDFs are read directly by AI</p>
                  </>
                )}
              </div>
            ) : (
              <Textarea
                value={pastedText} onChange={(e) => setPastedText(e.target.value)}
                placeholder="Paste the chapter / lesson text here…" rows={8} data-testid="oustaz-text-input"
              />
            )}

            <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">AI Engine</span>
                <Select value={aiProvider} onValueChange={setAiProvider}>
                  <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="oustaz-ai-provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="claude">Claude (Anthropic)</SelectItem>
                    <SelectItem value="openai">OpenAI</SelectItem>
                  </SelectContent>
                </Select>
                {selectedType === 'interactive_lesson' && (
                  <span className="text-xs text-muted-foreground">AI images need an OpenAI key (Settings). Diagrams use inline SVG.</span>
                )}
                {selectedType === 'infographic' && aiProvider === 'openai' && (
                  <span className="text-xs text-muted-foreground">Scanned/image PDFs need a Claude key or pasted text.</span>
                )}
              </div>
              <Button onClick={handleGenerate} disabled={generating || blockedByKeys} className="gap-2" data-testid="oustaz-generate-btn">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                {generating ? 'Generating…' : 'Generate'}
              </Button>
            </div>
          </Panel>

          {/* Share with a class (optional) */}
          <Panel className="p-4" data-testid="oustaz-share-panel">
            <h3 className="text-sm font-semibold mb-3">3. Share with a class <span className="font-normal text-muted-foreground">(optional)</span></h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Class</label>
                <Select value={targetClassId || 'none'} onValueChange={(v) => setTargetClassId(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="oustaz-class-select"><SelectValue placeholder="No class (just for me)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No class (just for me)</SelectItem>
                    {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {targetClassId && (
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Sections</label>
                  <div className="flex flex-wrap items-center gap-3 rounded-md border border-border p-2.5">
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <Checkbox checked={allSections} onCheckedChange={(v) => { setAllSections(!!v); if (v) setSelectedSectionIds([]); }} data-testid="oustaz-all-sections" />
                      All sections
                    </label>
                    {!allSections && (targetClass?.sections || []).map((s) => (
                      <label key={s.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <Checkbox
                          checked={selectedSectionIds.includes(s.id)}
                          onCheckedChange={(v) => setSelectedSectionIds((prev) => v ? [...prev, s.id] : prev.filter((x) => x !== s.id))}
                          data-testid={`oustaz-section-${s.id}`}
                        />
                        {s.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {targetClassId && (
              <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer w-fit">
                <Switch checked={visibleToStudents} onCheckedChange={setVisibleToStudents} data-testid="oustaz-visible-switch" />
                Visible to enrolled students
              </label>
            )}
          </Panel>

          {/* Progress */}
          {generating && job && (
            <Panel className="p-4" data-testid="oustaz-progress">
              <div className="flex items-center justify-between mb-2 text-sm">
                <span className="flex items-center gap-2 font-medium"><Loader2 className="w-4 h-4 animate-spin" />{STEP_LABELS[job.step] || job.step}</span>
                <span className="tabular-nums text-muted-foreground">{job.progress || 0}%</span>
              </div>
              <Progress value={job.progress || 0} className="h-2" />
              <p className="text-xs text-muted-foreground mt-2">This can take 30–90 seconds (longer for interactive lessons with images).</p>
            </Panel>
          )}

          {/* Result */}
          {result && (
            <Panel className="p-4" data-testid="oustaz-result">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <Badge className="mb-1">{TYPE_META[result.content_type]?.label || result.content_type}</Badge>
                  <p className="font-semibold">{result.title}</p>
                  <p className="text-xs text-muted-foreground">Ready to view & download</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="gap-2" onClick={() => openViewer(result)} data-testid="oustaz-result-open"><ExternalLink className="w-4 h-4" />Open</Button>
                  <Button className="gap-2" onClick={() => downloadZip(result.gen_id, result.title)} data-testid="oustaz-result-download"><Download className="w-4 h-4" />Download</Button>
                </div>
              </div>
            </Panel>
          )}
        </TabsContent>

        {/* BOOKS */}
        <TabsContent value="books" className="mt-4">
          <OustazBooks orgId={orgId} config={config} onOpenLibrary={() => { setTab('library'); loadGenerations(); }} />
        </TabsContent>

        {/* AI CHAT */}
        <TabsContent value="chat" className="mt-4">
          <OustazChat orgId={orgId} />
        </TabsContent>

        {/* LIBRARY */}
        <TabsContent value="library" className="mt-4">
          {!libLoading && generations.length > 0 && config?.is_admin && (
            <div className="flex justify-end mb-3">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={runAutoName} disabled={autoNaming} data-testid="oustaz-auto-name-btn">
                {autoNaming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Auto-name from content
              </Button>
            </div>
          )}
          {libLoading ? (
            <InlineSpinner />
          ) : generations.length === 0 ? (
            <EmptyState icon={Layers} title="No lessons yet" description="Generate your first piece of content from the Create tab." />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="oustaz-library-grid">
              {generations.map((g) => (
                <Panel key={g.gen_id} className="overflow-hidden" data-testid={`oustaz-gen-${g.gen_id}`}>
                  <div className="aspect-[5/3] bg-muted overflow-hidden border-b border-border">
                    {g.thumbnail_url ? <img src={fileUrl(g.thumbnail_url)} alt={g.title} className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="p-4">
                    <Badge variant="outline" className="mb-2" style={{ borderColor: TYPE_META[g.content_type]?.color, color: TYPE_META[g.content_type]?.color }}>
                      {g.content_type_label || g.content_type}
                    </Badge>
                    {g.is_draft && (
                      <Badge variant="secondary" className="mb-2 ml-1.5 bg-amber-100 text-amber-800 border border-amber-200" data-testid={`oustaz-draft-badge-${g.gen_id}`}>
                        Draft — AI Generated
                      </Badge>
                    )}
                    <div className="flex items-start gap-1.5">
                      <p className="text-sm font-semibold truncate flex-1" title={g.title}>{g.title}</p>
                      <button
                        type="button"
                        onClick={() => openRename(g)}
                        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-0.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        title="Rename lesson"
                        aria-label="Rename lesson"
                        data-testid={`oustaz-rename-${g.gen_id}`}
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {g.ai_provider && <Badge variant="secondary" className="text-[10px] font-normal capitalize">{g.ai_provider}</Badge>}
                      {g.class_name && (
                        <Badge variant="secondary" className="text-[10px] font-normal">
                          {g.class_name}{g.section_names?.length ? ` · ${g.section_names.join(', ')}` : ' · All'}
                        </Badge>
                      )}
                      {g.class_name && !g.visible_to_students && <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">Hidden</Badge>}
                      {g.source_book_title && (
                        <Badge variant="secondary" className="text-[10px] font-normal" title={`From ${g.source_book_title}`} data-testid={`oustaz-source-book-${g.gen_id}`}>
                          {g.source_book_title}{(g.source_page_from || g.source_page_to) ? ` · p${g.source_page_from}-${g.source_page_to}` : ''}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{new Date(g.created_at).toLocaleString()}</p>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" className="gap-1.5 flex-1" onClick={() => openViewer(g)} data-testid={`oustaz-open-${g.gen_id}`}><ExternalLink className="w-3.5 h-3.5" />Open</Button>
                      {g.is_draft && (
                        <Button size="sm" className="gap-1.5" onClick={() => handlePublish(g.gen_id)} disabled={publishingId === g.gen_id} data-testid={`oustaz-publish-${g.gen_id}`} title="Publish (remove draft flag)">
                          {publishingId === g.gen_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openShareDialog(g)} data-testid={`oustaz-share-${g.gen_id}`} title="Share with a class"><Share2 className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => downloadZip(g.gen_id, g.title)} data-testid={`oustaz-download-${g.gen_id}`}><Download className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setDeleteTarget(g)} data-testid={`oustaz-delete-${g.gen_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </Panel>
              ))}
            </div>
          )}
        </TabsContent>

        {/* PROMPTS (admin) */}
        {config?.is_admin && (
          <TabsContent value="prompts" className="mt-4">
            <OustazPrompts orgId={orgId} />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!viewerGen} onOpenChange={(o) => !o && setViewerGen(null)}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="oustaz-viewer-dialog">
          <DialogHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
            <div className="min-w-0 pr-4">
              <DialogTitle className="truncate text-base">{viewerGen?.title || 'Lesson'}</DialogTitle>
              <DialogDescription className="sr-only">Interactive resource preview</DialogDescription>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {viewerGen?.is_draft && (
                <Badge variant="secondary" className="bg-amber-100 text-amber-800 border border-amber-200 hidden sm:inline-flex">Draft — AI Generated</Badge>
              )}
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => openHtml(viewerGen?.html_url)} data-testid="oustaz-viewer-newtab">
                <ExternalLink className="w-3.5 h-3.5" /> New tab
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-white">
            {viewerGen && (
              <iframe
                title={viewerGen.title || 'lesson'}
                src={fileUrl(viewerGen.html_url)}
                className="w-full h-full border-0"
                sandbox="allow-scripts allow-popups allow-forms allow-modals allow-downloads"
                data-testid="oustaz-viewer-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this content?</AlertDialogTitle>
            <AlertDialogDescription>“{deleteTarget?.title}” will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="oustaz-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!shareTarget} onOpenChange={(o) => { if (!o) setShareTarget(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="oustaz-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Share2 className="w-4 h-4 text-primary" />Share lesson</DialogTitle>
            <DialogDescription className="truncate">{shareTarget?.title}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Class</label>
              <Select value={shareClassId || 'none'} onValueChange={(v) => { const nv = v === 'none' ? '' : v; setShareClassId(nv); setShareAllSections(true); setShareSectionIds([]); }}>
                <SelectTrigger data-testid="oustaz-share-class-select"><SelectValue placeholder="Not shared" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not shared (just for me)</SelectItem>
                  {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {shareClassId && (
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Sections</label>
                <div className="flex flex-wrap items-center gap-3 rounded-md border border-border p-2.5">
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <Checkbox checked={shareAllSections} onCheckedChange={(v) => { setShareAllSections(!!v); if (v) setShareSectionIds([]); }} data-testid="oustaz-share-all-sections" />
                    All sections
                  </label>
                  {!shareAllSections && (shareClass?.sections || []).map((s) => (
                    <label key={s.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <Checkbox
                        checked={shareSectionIds.includes(s.id)}
                        onCheckedChange={(v) => setShareSectionIds((prev) => v ? [...prev, s.id] : prev.filter((x) => x !== s.id))}
                        data-testid={`oustaz-share-section-${s.id}`}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {shareClassId && (
              <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
                <Switch checked={shareVisible} onCheckedChange={setShareVisible} data-testid="oustaz-share-visible-switch" />
                Visible to enrolled students
              </label>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            {shareTarget?.class_id && (
              <Button variant="outline" className="text-destructive hover:text-destructive mr-auto gap-1.5" onClick={removeSharing} data-testid="oustaz-share-remove">
                <X className="w-3.5 h-3.5" />Remove sharing
              </Button>
            )}
            <Button variant="outline" onClick={() => setShareTarget(null)} data-testid="oustaz-share-cancel">Cancel</Button>
            <Button onClick={saveSharing} disabled={shareSaving} className="gap-1.5" data-testid="oustaz-share-save">
              {shareSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameTarget} onOpenChange={(o) => { if (!o) setRenameTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="oustaz-rename-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit3 className="w-4 h-4 text-primary" />Rename lesson</DialogTitle>
            <DialogDescription>Give this lesson a clear, descriptive title.</DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <label className="text-xs text-muted-foreground mb-1 block">Title</label>
            <Input
              value={renameValue}
              maxLength={120}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && renameValue.trim()) saveRename(); }}
              placeholder="e.g. The Human Body & Its Vital Functions"
              autoFocus
              data-testid="oustaz-rename-input"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setRenameTarget(null)} data-testid="oustaz-rename-cancel">Cancel</Button>
            <Button onClick={saveRename} disabled={renameSaving || !renameValue.trim()} className="gap-1.5" data-testid="oustaz-rename-save">
              {renameSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Admin Prompt Management ──────────────────────────────────────────────
function OustazPrompts({ orgId }) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [savingType, setSavingType] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await oustazApi.listPrompts(orgId);
      setPrompts(data);
      setDrafts(Object.fromEntries(data.map((p) => [p.content_type, p.prompt])));
    } catch { toast.error('Failed to load prompts'); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { if (orgId) load(); }, [orgId, load]);

  const save = async (ct) => {
    setSavingType(ct);
    try {
      await oustazApi.updatePrompt(ct, { organization_id: orgId, prompt: drafts[ct] });
      toast.success('Prompt saved');
      load();
    } catch { toast.error('Save failed'); }
    finally { setSavingType(null); }
  };

  const reset = async (ct) => {
    try {
      const r = await oustazApi.resetPrompt(ct, orgId);
      setDrafts((d) => ({ ...d, [ct]: r.prompt }));
      toast.success('Reset to default');
      load();
    } catch { toast.error('Reset failed'); }
  };

  if (loading) return <InlineSpinner />;

  return (
    <div className="space-y-4" data-testid="oustaz-prompts">
      <p className="text-sm text-muted-foreground">Customize the AI instructions for each content type. Use <code className="px-1 bg-muted rounded">{'{chapter_text}'}</code> where the source text should be inserted.</p>
      {prompts.map((p) => (
        <Panel key={p.content_type} className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">{TYPE_META[p.content_type]?.label || p.label}</span>
              <Badge variant="outline" className="text-xs">{p.ai_provider}</Badge>
              {p.is_default && <Badge variant="secondary" className="text-xs">default</Badge>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => reset(p.content_type)} data-testid={`oustaz-prompt-reset-${p.content_type}`}><RefreshCw className="w-3.5 h-3.5" />Reset</Button>
              <Button size="sm" className="gap-1.5" disabled={savingType === p.content_type} onClick={() => save(p.content_type)} data-testid={`oustaz-prompt-save-${p.content_type}`}>
                {savingType === p.content_type ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}Save
              </Button>
            </div>
          </div>
          <Textarea rows={6} value={drafts[p.content_type] ?? ''} onChange={(e) => setDrafts((d) => ({ ...d, [p.content_type]: e.target.value }))} data-testid={`oustaz-prompt-text-${p.content_type}`} className="font-mono text-xs" />
        </Panel>
      ))}
    </div>
  );
}
